import { fmt } from '@/lib/utils';
import type { CashFlowYear, SensitivityResult } from '@lcca-nexus/shared';
import { Card, CardHeader, CardBody } from '@/components/ui/index';
import { Button } from '@/components/ui/Button';
import { ChevronDown, ChevronUp, TrendingUp, TrendingDown } from 'lucide-react';
import { cn } from '@/lib/utils';

// ── Cash Flow Table ───────────────────────────────────────────────────────────

interface CashFlowTableProps {
  cashFlows: CashFlowYear[];
  showAll: boolean;
  onToggleShowAll: () => void;
}

export function CashFlowTable({ cashFlows, showAll, onToggleShowAll }: CashFlowTableProps) {
  const displayed = showAll ? cashFlows : cashFlows.slice(0, 10);

  return (
    <Card className="animate-fade-up">
      <CardHeader>
        <div className="font-display font-bold text-navy-900 text-sm flex-1">
          📅 Year-by-Year Cash Flows
        </div>
        <Button
          size="sm"
          variant="secondary"
          icon={showAll ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          onClick={onToggleShowAll}
        >
          {showAll ? 'Show Less' : `Show All ${cashFlows.length} Years`}
        </Button>
      </CardHeader>
      <div className="overflow-x-auto">
        <table className="data-table">
          <thead>
            <tr>
              <th className="!text-left">Year</th>
              <th>O&M ($)</th>
              <th>Energy ($)</th>
              <th>Replacement ($)</th>
              <th>Total Outflow ($)</th>
              <th>Disc. Factor</th>
              <th>Present Worth ($)</th>
              <th>Cumulative PW ($)</th>
            </tr>
          </thead>
          <tbody>
            {displayed.map(y => (
              <tr key={y.year} className={y.replacementCost > 0 ? 'bg-red-50/40' : ''}>
                <td className="!font-mono !text-navy-600 !font-medium">
                  {y.year}
                  {y.replacementCost > 0 && (
                    <span className="ml-1.5 text-[10px] bg-red-100 text-red-600 rounded px-1 font-sans font-semibold">
                      REPL
                    </span>
                  )}
                </td>
                <td>{fmt(y.omCost)}</td>
                <td>{fmt(y.energyCost)}</td>
                <td className={y.replacementCost > 0 ? 'font-bold text-red-700' : ''}>{
                  y.replacementCost > 0 ? fmt(y.replacementCost) : '—'
                }</td>
                <td className="font-semibold">{fmt(y.totalCost)}</td>
                <td className="text-navy-500">{y.discountFactor.toFixed(4)}</td>
                <td>{fmt(y.presentWorth)}</td>
                <td className="text-navy-600 font-semibold">{fmt(y.cumulativePW)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!showAll && cashFlows.length > 10 && (
        <div className="px-5 py-3 text-center text-xs text-navy-400 border-t border-parchment-100">
          Showing 10 of {cashFlows.length} years.{' '}
          <button onClick={onToggleShowAll} className="text-navy-600 font-semibold underline">
            Show all
          </button>
        </div>
      )}
    </Card>
  );
}

// ── Sensitivity Table ─────────────────────────────────────────────────────────

interface SensitivityTableProps {
  sensitivity: SensitivityResult[];
  baseLCC: number;
}

export function SensitivityTable({ sensitivity, baseLCC }: SensitivityTableProps) {
  const maxDelta = Math.max(...sensitivity.map(s => Math.abs(s.deltaPercent)));

  return (
    <Card className="animate-fade-up">
      <CardHeader>
        <div>
          <div className="font-display font-bold text-navy-900 text-sm">
            📡 Sensitivity Analysis
          </div>
          <div className="text-xs text-navy-400 mt-0.5">Impact of ±10% change in each parameter on Total LCC</div>
        </div>
      </CardHeader>
      <div className="overflow-x-auto">
        <table className="data-table">
          <thead>
            <tr>
              <th className="!text-left">Parameter</th>
              <th>Change</th>
              <th>New LCC ($)</th>
              <th>Δ LCC ($)</th>
              <th>Δ LCC (%)</th>
              <th style={{ width: '180px' }}>Impact</th>
            </tr>
          </thead>
          <tbody>
            {sensitivity.map((s, i) => {
              const isUp   = s.deltaPercent > 0;
              const barPct = Math.abs(s.deltaPercent) / maxDelta * 100;
              return (
                <tr key={i}>
                  <td>
                    <div className="flex items-center gap-1.5">
                      {isUp
                        ? <TrendingUp size={13} className="text-red-500" />
                        : <TrendingDown size={13} className="text-teal-600" />
                      }
                      {s.label}
                    </div>
                  </td>
                  <td>
                    <span className={cn(
                      'badge',
                      s.perturbation === '+10%' ? 'bg-amber-100 text-amber-800' : 'bg-teal-100 text-teal-800'
                    )}>
                      {s.perturbation}
                    </span>
                  </td>
                  <td>{fmt(s.newLCC)}</td>
                  <td className={isUp ? 'text-red-600 font-semibold' : 'text-teal-700 font-semibold'}>
                    {isUp ? '+' : ''}{fmt(s.deltaLCC)}
                  </td>
                  <td className={isUp ? 'text-red-600 font-semibold' : 'text-teal-700 font-semibold'}>
                    {isUp ? '+' : ''}{s.deltaPercent.toFixed(2)}%
                  </td>
                  <td>
                    <div className="flex items-center gap-1.5">
                      <div className="flex-1 bg-parchment-200 rounded-full h-2">
                        <div
                          className={cn('h-2 rounded-full', isUp ? 'bg-red-400' : 'bg-teal-400')}
                          style={{ width: `${barPct}%` }}
                        />
                      </div>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
